// Localização: frontend/src/components/Prontuario/PacienteTarefasConcluidasTab.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAgendaTarefas } from '../../hooks/useAgendaTarefas';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import TarefaStatusFilter from '../AgendaTarefas/TarefaStatusFilter';
import TarefaTipoFilter from '../AgendaTarefas/TarefaTipoFilter';
import TarefaProfissionalFilter from '../AgendaTarefas/TarefaProfissionalFilter';
import PrimaryButton from '../Common/PrimaryButton';
import { CheckCircle, User, FileText, Pill, Calendar, Tag } from 'lucide-react';

const PacienteTarefasConcluidasTab = ({ pacienteId }) => {
    const { tarefas, isLoadingTarefas, errorTarefas, fetchTarefasConcluidas } = useAgendaTarefas(null);
    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroTipo, setFiltroTipo] = useState([]);
    const [filtroProfissionais, setFiltroProfissionais] = useState([]);
    
    // Estados para filtros flutuantes
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    
    const [showTipoFilter, setShowTipoFilter] = useState(false);
    const tipoFilterRef = useRef(null);
    const tipoButtonRef = useRef(null);
    
    const [showProfissionalFilter, setShowProfissionalFilter] = useState(false);
    const profissionalFilterRef = useRef(null);
    const profissionalButtonRef = useRef(null);

    // Carregar tarefas ao montar o componente
    useEffect(() => {
        if (pacienteId) {
            fetchTarefasConcluidas(
                pacienteId,
                null,
                null
            ).catch(err => {
                console.error('Erro ao carregar tarefas concluídas:', err);
            });
        }
    }, [pacienteId, fetchTarefasConcluidas]);

    // Aplicar filtros aos dados exibidos
    const filteredTarefas = useMemo(() => {
        let filtered = [...tarefas];
        
        // Filtrar por profissional se selecionado
        if (filtroProfissionais && filtroProfissionais.length > 0) {
            const profIds = filtroProfissionais.map(id => id.toString());
            filtered = filtered.filter(t => 
                t.usuario_id && profIds.includes(t.usuario_id.toString())
            );
        }
        
        // Filtrar por status se selecionado
        if (filtroStatus && filtroStatus.length > 0) {
            filtered = filtered.filter(t => {
                const statusLower = t.status?.toLowerCase() || 'concluída';
                return filtroStatus.some(s => statusLower.includes(s.toLowerCase()));
            });
        }
        
        // Filtrar por tipo se selecionado
        if (filtroTipo && filtroTipo.length > 0) {
            filtered = filtered.filter(t => {
                const tipoTarefa = t.tipo_tarefa || '';
                return filtroTipo.includes(tipoTarefa);
            });
        }
        
        return filtered;
    }, [tarefas, filtroStatus, filtroTipo, filtroProfissionais]);

    // Fechar dropdowns ao clicar fora
    useEffect(() => {
        if (!showStatusFilter && !showTipoFilter && !showProfissionalFilter) return;

        const handleClickOutside = (event) => {
            if (
                (showStatusFilter && statusFilterRef.current && statusButtonRef.current &&
                    !statusFilterRef.current.contains(event.target) &&
                    !statusButtonRef.current.contains(event.target)) ||
                (showTipoFilter && tipoFilterRef.current && tipoButtonRef.current &&
                    !tipoFilterRef.current.contains(event.target) &&
                    !tipoButtonRef.current.contains(event.target)) ||
                (showProfissionalFilter && profissionalFilterRef.current && profissionalButtonRef.current &&
                    !profissionalFilterRef.current.contains(event.target) &&
                    !profissionalButtonRef.current.contains(event.target))
            ) {
                setShowStatusFilter(false);
                setShowTipoFilter(false);
                setShowProfissionalFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter, showTipoFilter, showProfissionalFilter]);

    // Fechar dropdowns ao pressionar Escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
                setShowTipoFilter(false);
                setShowProfissionalFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // Formatação de data/hora
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Obter ícone baseado no tipo de tarefa
    const getTarefaIcon = (tipoTarefa) => {
        switch (tipoTarefa) {
            case 'Medicação':
                return <Pill className="w-5 h-5 text-purple-600" />;
            case 'Higiene':
                return <FileText className="w-5 h-5 text-emerald-600" />;
            default:
                return <CheckCircle className="w-5 h-5 text-green-600" />;
        }
    };

    // Obter cor baseada no tipo de tarefa
    const getTarefaColor = (tipoTarefa) => {
        switch (tipoTarefa) {
            case 'Medicação':
                return 'bg-purple-50 border-purple-200';
            case 'Higiene':
                return 'bg-emerald-50 border-emerald-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    return (
        <div className="relative space-y-4">
            {/* Botões Flutuantes à Direita - Filtros */}
            <div className="sticky top-0 self-end z-30 flex flex-row gap-2 mb-4 w-fit ml-auto">
                {/* Botão de Status */}
                <div ref={statusButtonRef} className="relative">
                    <PrimaryButton
                        onClick={() => {
                            setShowStatusFilter(!showStatusFilter);
                            setShowTipoFilter(false);
                            setShowProfissionalFilter(false);
                        }}
                        active={showStatusFilter}
                        variant="primary"
                        size="md"
                        icon={<Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />}
                        title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
                    >
                        <span className="hidden sm:inline whitespace-nowrap">Status</span>
                    </PrimaryButton>
                    {/* Badge indicando filtro ativo */}
                    {filtroStatus && filtroStatus.length > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                            <span className="text-[8px] text-white font-bold">{filtroStatus.length}</span>
                        </span>
                    )}
                    {/* Filtro de Status Flutuante - Dropdown */}
                    {showStatusFilter && (
                        <div ref={statusFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[200px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <TarefaStatusFilter
                                status={filtroStatus}
                                onStatusChange={(newStatus) => {
                                    setFiltroStatus(newStatus);
                                }}
                                onClose={() => setShowStatusFilter(false)}
                            />
                        </div>
                    )}
                </div>

                {/* Botão de Tipo */}
                <div ref={tipoButtonRef} className="relative">
                    <PrimaryButton
                        onClick={() => {
                            setShowTipoFilter(!showTipoFilter);
                            setShowStatusFilter(false);
                            setShowProfissionalFilter(false);
                        }}
                        active={showTipoFilter}
                        variant="primary"
                        size="md"
                        icon={<Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showTipoFilter ? 'scale-110' : ''} transition-transform`} />}
                        title={showTipoFilter ? 'Ocultar filtro de tipo' : 'Mostrar filtro de tipo'}
                    >
                        <span className="hidden sm:inline whitespace-nowrap">Tipo</span>
                    </PrimaryButton>
                    {/* Filtro de Tipo de Tarefa Flutuante - Dropdown */}
                    {showTipoFilter && (
                        <div ref={tipoFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[220px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <TarefaTipoFilter
                                tipos={filtroTipo}
                                onTiposChange={(newTipos) => {
                                    setFiltroTipo(newTipos);
                                }}
                                onClose={() => setShowTipoFilter(false)}
                            />
                        </div>
                    )}
                </div>

                {/* Botão de Profissional */}
                <div ref={profissionalButtonRef} className="relative">
                    <PrimaryButton
                        onClick={() => {
                            setShowProfissionalFilter(!showProfissionalFilter);
                            setShowStatusFilter(false);
                            setShowTipoFilter(false);
                        }}
                        active={showProfissionalFilter}
                        variant="primary"
                        size="md"
                        icon={<User className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showProfissionalFilter ? 'scale-110' : ''} transition-transform`} />}
                        title={showProfissionalFilter ? 'Ocultar filtro de profissional' : 'Mostrar filtro de profissional'}
                    >
                        <span className="hidden sm:inline whitespace-nowrap">Profissional</span>
                    </PrimaryButton>
                    {/* Filtro de Profissional Flutuante - Dropdown */}
                    {showProfissionalFilter && (
                        <div ref={profissionalFilterRef} className="absolute top-full right-0 mt-2 z-[100] w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[220px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <TarefaProfissionalFilter
                                profissionais={filtroProfissionais}
                                onProfissionaisChange={(newProfissionais) => {
                                    setFiltroProfissionais(newProfissionais);
                                }}
                                onClose={() => setShowProfissionalFilter(false)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Conteúdo */}
            <div className="pb-8 space-y-6">
                {/* Loading */}
                {isLoadingTarefas && <LoadingIndicator message="Carregando tarefas concluídas..." />}

            {/* Erro */}
            {errorTarefas && <Alert type="error" message={errorTarefas} />}

            {/* Lista de Tarefas */}
            {!isLoadingTarefas && !errorTarefas && (
                <Card title={`Tarefas Concluídas (${filteredTarefas.length} ${filteredTarefas.length === 1 ? 'tarefa' : 'tarefas'})`}>
                    {filteredTarefas.length === 0 ? (
                        <div className="text-center py-10">
                            <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">
                                Nenhuma tarefa concluída encontrada.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Tipo
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Descrição
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Agendada para
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Concluída em
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cuidador
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Observações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredTarefas.map((tarefa) => (
                                            <tr key={tarefa.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-2">
                                                        {getTarefaIcon(tarefa.tipo_tarefa)}
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {tarefa.tipo_tarefa || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="text-sm text-gray-900">
                                                        {tarefa.instrucoes_tarefa || tarefa.descricao || 'N/A'}
                                                    </div>
                                                    {tarefa.nome_medicamento && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            <Pill className="w-3 h-3 inline mr-1" />
                                                            {tarefa.nome_medicamento}
                                                            {tarefa.dose && (
                                                                <span className="ml-1">
                                                                    - {tarefa.dose} {tarefa.unidade_medida || ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {formatDate(tarefa.hora_agendada)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {formatTime(tarefa.hora_agendada)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-green-600">
                                                        {formatDateTime(tarefa.hora_conclusao)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm text-gray-900">
                                                            {tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="text-sm text-gray-600 max-w-xs truncate">
                                                        {tarefa.observacoes_conclusao || '-'}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="md:hidden space-y-4">
                                {filteredTarefas.map((tarefa) => (
                                    <div
                                        key={tarefa.id}
                                        className={`border rounded-lg p-4 ${getTarefaColor(tarefa.tipo_tarefa)}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center space-x-2">
                                                {getTarefaIcon(tarefa.tipo_tarefa)}
                                                <span className="font-semibold text-gray-900">
                                                    {tarefa.tipo_tarefa || 'N/A'}
                                                </span>
                                            </div>
                                            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                                                Concluída
                                            </span>
                                        </div>

                                        <div className="space-y-2">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {tarefa.instrucoes_tarefa || tarefa.descricao || 'N/A'}
                                                </p>
                                                {tarefa.nome_medicamento && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        <Pill className="w-3 h-3 inline mr-1" />
                                                        {tarefa.nome_medicamento}
                                                        {tarefa.dose && (
                                                            <span className="ml-1">
                                                                - {tarefa.dose} {tarefa.unidade_medida || ''}
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                                <div className="flex items-center space-x-1">
                                                    <Calendar className="w-3 h-3" />
                                                    <span>
                                                        Agendada: {formatDate(tarefa.hora_agendada)} {formatTime(tarefa.hora_agendada)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    <CheckCircle className="w-3 h-3 text-green-600" />
                                                    <span className="text-green-600 font-medium">
                                                        Concluída: {formatDateTime(tarefa.hora_conclusao)}
                                                    </span>
                                                </div>
                                            </div>

                                            {(tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel) && (
                                                <div className="flex items-center space-x-1 text-xs text-gray-600">
                                                    <User className="w-3 h-3" />
                                                    <span>Cuidador: {tarefa.cuidador_responsavel_nome || tarefa.cuidador_responsavel}</span>
                                                </div>
                                            )}

                                            {tarefa.observacoes_conclusao && (
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <p className="text-xs font-medium text-gray-700 mb-1">Observações:</p>
                                                    <p className="text-xs text-gray-600 whitespace-pre-wrap">
                                                        {tarefa.observacoes_conclusao}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Card>
            )}
            </div>
        </div>
    );
};

export default PacienteTarefasConcluidasTab;

